<script setup>
import {computed, onMounted, ref, watch} from "vue";
import {useRoute, useRouter} from "vue-router";
import {useTitle} from "vue-page-title";
import {useI18n} from 'vue-i18n';
import createApiService from "@/services/api.js";
import {eachDayOfInterval, format, getDay, getWeek} from "date-fns";
import {getHolidayDisplayNames, holidayTypeColors, specialHoursTypeColors, themeTintStyle} from "@/helpers/constants.js";
import WorkdayEditDialog from "@/components/time_registration/WorkdayEditDialog.vue";
import SpecialHoursEditDialog from "@/components/time_registration/SpecialHoursEditDialog.vue";
import WorkdayLineDialog from "@/components/time_registration/WorkdayLineDialog.vue";
import WorkdayProjectSplit from "@/components/time_registration/WorkdayProjectSplit.vue";
import {assignLine, cleanAssignment, effectiveAssignment, projectColor, projectLetter} from "@/helpers/projectSplit.js";
import {castTimeForDisplay, isNextDay, timeInMinutes} from "@/helpers/helpers.js";
import {useUnsavedChangesPrompt} from "@/composables/useUnsavedChangesPrompt.js";
import {useSnackbarStore} from "@/stores/snackbar.js";
import LogsDisplay from "@/components/LogsDisplay.vue";
import WorkdaySummary from "@/components/time_registration/WorkdaySummary.vue";
import BalanceMutationsDialog from "@/components/time_registration/BalanceMutationsDialog.vue";
import LockMonthDialog from "@/components/time_registration/LockMonthDialog.vue";
import { useFilters } from '@/composables/useFilters.js';

const { t } = useI18n();

const { formatDate, ucFirst, formatTime, formatDistance } = useFilters();

const holidayDisplayNames = computed(() => getHolidayDisplayNames(t));
const router = useRouter();
const api = createApiService(router);
const {setTitle} = useTitle();
const route = useRoute();
const summary = ref();
const workdays = ref([]);
const projects = ref();
const changedWorkdays = ref({});
const loading = ref({});
const user = ref();
const openDays = ref([]);
const holidays = ref();
const snackbarStore = useSnackbarStore();

const hasUnsavedChanges = computed(() => !!Object.keys(changedWorkdays.value).length);
useUnsavedChangesPrompt(hasUnsavedChanges);

definePage({
  name: "time-registration-details",
  meta: {
    requiresAuth: true,
  },
});

onMounted(async () => {
  await refreshWorkdays();
  await refreshProjects();
  await handleInitialHash();
});

const latestUsedParams = ref();
const refreshWorkdays = async () => {
  changedWorkdays.value = {};

  try {
    latestUsedParams.value = {
      employee_id: route.params.employee,
      year: route.params.year,
      month: route.params.month,
    };
    const workdayParams = new URLSearchParams(latestUsedParams.value).toString();

    const [holidaysResponse, employeeResponse, workdaysResponse] = await Promise.all([
      holidays.value ?? api.get("getHolidays"),
      api.get(`employeeGet?id=${route.params.employee}`),
      api.get(`workdayList?${workdayParams}`)
    ]);

    holidays.value ??= holidaysResponse.data.data;
    user.value = employeeResponse.data.data;
    setTitle(user.value.name);

    workdays.value = [];
    openDays.value = [];

    // Use the user start and end date if they are within the displayed month, otherwise use the start and end of the month.
    let startDate = new Date(route.params.year, route.params.month - 1, 1);
    let endDate = new Date(route.params.year, route.params.month, 0);
    const userStartDate = new Date(user.value.start_date);
    const userEndDate = new Date(user.value.end_date);
    if (user.value.start_date && userStartDate.getFullYear() === parseInt(route.params.year) && userStartDate.getMonth() === parseInt(route.params.month) - 1) {
      startDate = userStartDate;
    }
    if (user.value.end_date && userEndDate.getFullYear() === parseInt(route.params.year) && userEndDate.getMonth() === parseInt(route.params.month) - 1) {
      endDate = userEndDate;
    }

    eachDayOfInterval({
      start: startDate,
      end: endDate,
    }).forEach((date) => {
      const workday = getWorkday(date, workdaysResponse.data.data);
      workdays.value.push(workday);
      if (workday.status === 'new' && workday.id) {
        openDays.value.push(workdays.value.length - 1);
      }
    });

    await refreshWorkdaySummary();
  } catch (error) {
    console.log(error);
  }
};

const refreshSpecialHours = async (workday) => {
  try {
    const response = await api.get(`workdaySpecialHoursList?workday_id=${workday.id}`);

    const workdayLocal = workdays.value.find(w => w.id === workday.id);
    if (!workdayLocal) return;

    workdayLocal.specialHours = response.data.data;
    await updateCalculation(workdayLocal);
  } catch (error) {
    console.log(error);
  }
};

const refreshProjects = async () => {
  const params = new URLSearchParams({
    employee_id: route.params.employee,
    year: route.params.year,
    month: route.params.month,
  }).toString();

  api.get(`projectList?${params}`)
    .then((response) => {
      projects.value = response.data.data;
    })
    .catch((error) => {
      console.log(error);
    });
};

const getProjectsOnDate = (date) => {
  if (!projects.value) {
    return [];
  }

  // In planned order: by the start of the event, then by name.
  return projects.value
    .filter((project) => getEventFromProject(project, date))
    .sort((a, b) => getEventFromProject(a, date).start.localeCompare(getEventFromProject(b, date).start) || a.name.localeCompare(b.name));
};

const getEventFromProject = (project, date, type = null) => {
  return project.events.find((event) => {
    return (!type || event.type === type) && format(event.start, 'yyyy-MM-dd') === date && event.employees.some((eventEmployee) => eventEmployee.employee_id === parseInt(route.params.employee));
  });
};

// In backend order, which sets their colors and letters.
const getDayProjects = (day) => (day.plannedProjectIds ?? []).map((id) => {
  const project = projects.value?.find((project) => project.id === id);
  const event = project && getEventFromProject(project, day.date, 'work');
  return {
    id,
    name: project?.name ?? '',
    plannedStart: event ? format(event.start, 'HH:mm:ss') : null,
    plannedEnd: event?.end ? format(event.end, 'HH:mm:ss') : null,
  };
});

const hasMultipleProjects = (day) => day.id && day.lines.length > 0 && (day.plannedProjectIds ?? []).length > 1;

// Which project each line belongs to, as {lineId: projectId}, from the project_id of the lines.
const getProjectAssignment = (day) => cleanAssignment(
  day.lines,
  Object.fromEntries(day.lines.map((line) => [line.id, line.project_id])),
  day.plannedProjectIds ?? [],
);

// The project every line belongs to: a line without a project of its own belongs to the project before it.
const getEffectiveAssignment = (day) => effectiveAssignment(day.lines, getProjectAssignment(day));

const getLineProjectIndex = (day, line) => getDayProjects(day).findIndex((project) => project.id === getEffectiveAssignment(day)[line.id]);

const isProjectInherited = (day, line) => !getProjectAssignment(day)[line.id];

const getProjectIconTitle = (day, line, project, index) => {
  if (getLineProjectIndex(day, line) === index && isProjectInherited(day, line)) {
    return t('timeRegistration.workday.projectSplit.inherited', {project: project.name});
  }
  return day.status === 'new' ? t('timeRegistration.workday.projectSplit.assignTo', {project: project.name}) : project.name;
};

// Clicking the project a line already belongs to unassigns it.
const assignLineToProject = (day, line, project) => {
  const assignment = getProjectAssignment(day);
  const newAssignment = assignLine(day.lines, assignment, line.id, assignment[line.id] === project.id ? null : project.id);
  day.lines.forEach((dayLine) => {
    dayLine.project_id = newAssignment[dayLine.id] ?? null;
  });
  changedWorkdays.value[day.date] = true;
  updateCalculation(day);
};

// The data saved per line, with the project it belongs to. Voided lines and lines of projects no longer planned on this
// day lose their project.
const getWorkdayLinesData = (workday) => {
  const assignment = hasMultipleProjects(workday) ? getEffectiveAssignment(workday) : {};
  return Object.fromEntries(workday.lines.map((line) => [line.id, {
    void: line.void,
    project_id: assignment[line.id] ?? null,
  }]));
};

const getProjectIndex = (day, project) => getDayProjects(day).findIndex((dayProject) => dayProject.id === project.id);

const getWorkday = (date, workdays) => {
  const workday = workdays.find((workday) => workday.date === format(date, 'yyyy-MM-dd'));
  if (workday) {
    return workday;
  }
  else {
    return {
      id: null,
      date: format(date, 'yyyy-MM-dd'),
      status: 'new',
      lines: [],
      travel_to_end_line_id: null,
      travel_from_start_line_id: null,
      remark: '',
    };
  }
};

const refreshWorkdaySummary = async () => {
  try {
    const reponse = await api.get("workdaySummary?" + new URLSearchParams({
      employee_id: route.params.employee,
      year: route.params.year,
      month: route.params.month,
    }).toString());
    summary.value = reponse.data.data;
  } catch (error) {
    console.log(error);
  }
};

// The latest calculation request per date; older responses are ignored, so quick changes can't be undone by them.
const calculationRequests = {};

const updateCalculation = async (workday) => {
  let data = JSON.parse(JSON.stringify(workday));
  data.workdayLinesData = getWorkdayLinesData(workday);

  const request = (calculationRequests[workday.date] ?? 0) + 1;
  calculationRequests[workday.date] = request;
  loading.value[workday.date] = true;
  try {
    const response = await api.post("workdayCalculate?id=" + workday.id, JSON.stringify(data));
    if (request !== calculationRequests[workday.date]) {
      return;
    }
    const workdayIndex = workdays.value.findIndex((workdayLocal) => workdayLocal.id === workday.id);
    workdays.value[workdayIndex] = response.data.data;
  } catch(error) {
    console.log(error);
  } finally {
    loading.value[workday.date] = false;
  }
};

const saveWorkday = async (workday, refreshSummary = true) => {
  loading.value[workday.date] = true;

  try {
    if (workday.specialHours) {
      const deletionPromises = workday.specialHours
        .filter(specialHours => specialHours.markedForDeletion && specialHours.id)
        .map(specialHours => api.delete(`workdaySpecialHoursDelete?id=${specialHours.id}`));

      if (deletionPromises.length > 0) {
        await Promise.all(deletionPromises);
      }
    }

    const workdayLinesData = getWorkdayLinesData(workday);

    let json = JSON.stringify({
      employee_id: route.params.employee,
      date: workday.date,
      travel_to_end_line_id: workday.travel_to_end_line_id,
      travel_from_start_line_id: workday.travel_from_start_line_id,
      workdayLinesData: workdayLinesData,
      remark: workday.remark,
      status: workday.status,
      break_time: workday.break_time,
      work_time: workday.work_time,
    });

    const response = await api.post((workday.id) ? "workdayUpdate?id=" + workday.id : "workdayCreate", json);

    const workdayIndex = workdays.value.findIndex((workdayLocal) => workdayLocal.date === workday.date);
    workdays.value[workdayIndex] = response.data.data;
    delete changedWorkdays.value[workday.date];
    if (workdays.value[workdayIndex].status === 'processed') {
      openDays.value = openDays.value.filter((day) => day !== workdayIndex);
    }
    if (refreshSummary) {
      await refreshWorkdaySummary();
    }
    loading.value[workday.date] = false;
  } catch (error) {
    loading.value[workday.date] = false;
    console.log(error);
  }
};

const updateWorkday = (workday, newWorkday) => {
  const workdayIndex = workdays.value.findIndex((workdayLocal) => workdayLocal.id === workday.id);

  // Preserve some data of the original workday.
  newWorkday.status = workday.status;
  newWorkday.remark = workday.remark;
  newWorkday.travel_from_start_line_id = workday.travel_from_start_line_id;
  newWorkday.travel_to_end_line_id = workday.travel_to_end_line_id;
  // Keep unsaved project assignments; a new line has no project yet.
  newWorkday.lines.forEach((line) => {
    line.project_id = workday.lines.find((oldLine) => oldLine.id === line.id)?.project_id ?? null;
  });

  workdays.value[workdayIndex] = newWorkday;

  if (workdays.value[workdayIndex].status === 'processed') {
    openDays.value = openDays.value.filter((day) => day !== workdayIndex);
  }

  updateCalculation(newWorkday);
  refreshWorkdaySummary();
};

const markSpecialHoursForDeletion = (workday, specialHours) => {
  specialHours.markedForDeletion = !specialHours.markedForDeletion;
  changedWorkdays.value[workday.date] = true;
};

const saveAllWorkdays = async () => {
  const savePromises = Object.keys(changedWorkdays.value).map((workdayDate) =>
    saveWorkday(workdays.value.find((workday) => workday.date === workdayDate), false)
  );
  await Promise.all(savePromises);
  await refreshWorkdaySummary();
};

const setTravelTo = (item) => {
  const workday = workdays.value.find((workday) => workday.id === item.workday_id);
  workday.travel_to_end_line_id = (workday.travel_to_end_line_id !== item.id) ? item.id : null;
  changedWorkdays.value[workday.date] = true;
  updateCalculation(workday);
};

const setTravelFrom = (item) => {
  const workday = workdays.value.find((workday) => workday.id === item.workday_id);
  workday.travel_from_start_line_id = (workday.travel_from_start_line_id !== item.id) ? item.id : null;
  changedWorkdays.value[workday.date] = true;
  updateCalculation(workday);
};

const voidLine = (item) => {
  item.void = !item.void;

  const workday = workdays.value.find((workday) => workday.id === item.workday_id);

  changedWorkdays.value[workday.date] = true;
  updateCalculation(workday);
};

const isNonWorkingDay = (date) => {
  const isEvenWeek = getWeek(date, {weekStartsOn: 1}) % 2 === 0;
  const nonWorkingDays = isEvenWeek ? user.value.non_working_days_even_weeks ?? [] : user.value.non_working_days_uneven_weeks ?? [];

  return nonWorkingDays.indexOf(format(date, 'eeee').toLowerCase()) !== -1;
};

const getIcon = (workday, color = false) => {
  if (!workday.id) {
    return (color) ? 'grey' : 'mdi-plus';
  }

  if (changedWorkdays.value[workday.date]) {
    return (color) ? 'indufastRed' : 'mdi-content-save';
  }

  if (workday.status === 'processed') {
    return (color) ? 'indufastGreen' : 'mdi-check';
  }

  return (color) ? 'text-black' : 'mdi-alert-box-outline';
};

const getClasses = (workday) => {
  let classes = [];
  if (new Date(workday.date).getDay() === 0 || new Date(workday.date).getDay() === 6) {
    classes.push('weekend');
  }

  if (!workday.id) {
    classes.push('new');
  }

  return classes;
};

const holidayColor = (date) => holidayTypeColors[holidays.value[date]?.type] ?? null;
const holidayTintStyle = (date) => themeTintStyle(holidayColor(date));
const specialHoursColor = (type) => specialHoursTypeColors[type] ?? 'indufastBlue';

const rowProps = (row, day) => {
  const props = {
    class: {
      "workday-line": true,
      "void": row.item.void,
      "travel-from": !row.item.void && row.item.type === 'travel-from',
      "travel-to": !row.item.void && row.item.type === 'travel-to',
    },
  };

  // Mark where the employee moved on to another project.
  if (hasMultipleProjects(day) && !row.item.void) {
    const assignment = getEffectiveAssignment(day);
    const previous = day.lines.slice(0, day.lines.indexOf(row.item)).findLast((line) => !line.void);
    const index = getLineProjectIndex(day, row.item);
    if (previous && index !== -1 && assignment[previous.id] !== assignment[row.item.id]) {
      props.class["project-start"] = true;
      props.style = {'--project-color': `var(--v-theme-${projectColor(index)})`};
    }
  }

  return props;
};

const specialHoursRowProps = (row) => {
  return {
    class: {
      "special-hours-line": true,
      "marked-for-deletion": row.item.markedForDeletion,
    },
  };
};

const dayHasData = (day) => {
  return day.travel_to_end_line_id || day.travel_from_start_line_id || day.travel_to_time || day.travel_to_distance || day.travel_from_time || day.travel_from_distance;
};

const getDayHeaders = (day) => hasMultipleProjects(day)
  ? [{title: t('timeRegistration.workday.tripHeaders.project'), value: "project", sortable: false, cellProps: {class: "no-wrap"}}, ...dayHeaders.value]
  : dayHeaders.value;

const dayHeaders = computed(() => [
  {title: t('timeRegistration.workday.tripHeaders.start'), value: "start", cellProps: {class: "no-wrap"}},
  {title: t('timeRegistration.workday.tripHeaders.end'), value: "end", cellProps: {class: "no-wrap"}},
  {title: t('timeRegistration.workday.tripHeaders.duration'), value: "duration", cellProps: {class: "no-wrap"}},
  {title: t('timeRegistration.workday.tripHeaders.vehicle'), value: "vehicle", cellProps: {class: "no-wrap"}},
  {title: t('timeRegistration.workday.tripHeaders.from'), value: "start_address"},
  {title: t('timeRegistration.workday.tripHeaders.to'), value: "end_address"},
  {title: t('timeRegistration.workday.tripHeaders.distance'), value: "distance", cellProps: {class: "no-wrap"}},
  {title: t('timeRegistration.workday.tripHeaders.actions'), value: "actions", cellProps: {class: "no-wrap"}},
]);

const specialHoursHeaders = computed(() => [
  {title: t('timeRegistration.workday.specialHoursHeaders.type'), value: "type"},
  {title: t('timeRegistration.workday.specialHoursHeaders.duration'), value: "duration", cellProps: {class: "no-wrap"}},
  {title: t('timeRegistration.workday.specialHoursHeaders.actions'), value: "actions", cellProps: {class: "no-wrap"}, align: "end"},
]);

const specialHoursTypes = computed(() => [
  {value: 'leave', title: t('timeRegistration.workday.specialHoursTypes.leave')},
  {value: 'special-leave', title: t('timeRegistration.workday.specialHoursTypes.specialLeave')},
  {value: 'sick', title: t('timeRegistration.workday.specialHoursTypes.sick')},
  {value: 'unexcused-leave', title: t('timeRegistration.workday.specialHoursTypes.unexcusedLeave')},
  {value: 'holiday', title: t('timeRegistration.workday.specialHoursTypes.holiday')},
  {value: 'collective-leave', title: t('timeRegistration.workday.specialHoursTypes.collectiveLeave')},
]);

const getSpecialHoursTypeLabel = (type) => {
  const typeObj = specialHoursTypes.value.find(t => t.value === type);
  return typeObj ? typeObj.title : type;
};

const getDaySummary = (day) => {
  let summary = [];

  if (day.workdayDurationNet) {
    summary.push(t('timeRegistration.workday.summary.hoursWorked') + ': ' + day.workdayDurationNet);
  }
  if (day.travelDurationNet && day.travelDurationNet !== '00:00:00') {
    summary.push(t('timeRegistration.workday.summary.travelTimeSummary') + ': ' + day.travelDurationNet);
  }
  if (day.specialHoursLeave) {
    summary.push(t('timeRegistration.workday.fields.leave') + ': ' + day.specialHoursLeave);
  }
  if (day.specialHoursSpecialLeave) {
    summary.push(t('timeRegistration.workday.fields.specialLeave') + ': ' + day.specialHoursSpecialLeave);
  }
  if (day.specialHoursSick) {
    summary.push(t('timeRegistration.workday.fields.sick') + ': ' + day.specialHoursSick);
  }
  if (day.specialHoursUnexcusedLeave) {
    summary.push(t('timeRegistration.workday.fields.unexcusedLeave') + ': ' + day.specialHoursUnexcusedLeave);
  }

  return summary.join(' - ');
};

const workdaySummary = computed(() => {
  return [
    [
      {title: t('timeRegistration.workday.summary.completedDays'), value: summary.value?.count},
      {title: t('timeRegistration.workday.summary.openDays'), value: openDayCount.value},
      {title: t('timeRegistration.workday.summary.totalWorkedHours'), value: summary.value?.total},
      {title: t('timeRegistration.workday.summary.hoursPerMonth'), value: summary.value?.hoursPerMonth},
      {title: t('timeRegistration.workday.summary.monthlyBalance'), value: summary.value?.monthlyBalance},
      {title: t('timeRegistration.workday.summary.totalBalance'), value: summary.value?.totalBalance},
    ],
    [
      {title: t('timeRegistration.workday.summary.saturdayHours'), value: summary.value?.totalSaturday},
      {title: t('timeRegistration.workday.summary.sundayOrHolidayHours'), value: summary.value?.totalSundayOrHoliday},
      {title: t('timeRegistration.workday.summary.overtimeBelow'), value: summary.value?.totalOvertimeBelow},
      {title: t('timeRegistration.workday.summary.overtimeAbove'), value: summary.value?.totalOvertimeAbove},
      {title: t('timeRegistration.workday.summary.shiftedHours'), value: summary.value?.totalOutsideWorkingHours},
      {title: t('timeRegistration.workday.summary.travelTime'), value: summary.value?.totalTravel},
    ],
    [
      {title: t('timeRegistration.workday.summary.leave'), value: summary.value?.totalLeave},
      {title: t('timeRegistration.workday.summary.specialLeave'), value: summary.value?.totalSpecialLeave},
      {title: t('timeRegistration.workday.summary.sick'), value: summary.value?.totalSick},
      {title: t('timeRegistration.workday.summary.unexcusedLeave'), value: summary.value?.totalUnexcusedLeave},
    ],
  ];
});

watch(route, () => {
  if (route.params.month !== latestUsedParams.value.month) {
    if (hasUnsavedChanges.value) {
      if (!confirm(t('timeRegistration.unsavedChanges'))) {
        router.replace({
          name: 'time-registration-details',
          params: {
            employee: latestUsedParams.value.employee_id,
            year: latestUsedParams.value.year,
            month: latestUsedParams.value.month,
          }
        });
        return;
      }
    }
    refreshWorkdays();
    refreshProjects();
  }
});

const isHoliday = (date) => {
  return Object.keys(holidays.value).includes(date);
};
const isCollectiveLeave = (date) => {
  return holidays.value[date]?.type === 'collective_leave';
};
const isClosingDay = (date) => {
  return holidays.value[date]?.type === 'closing_day';
};
const getHolidayName = (date) => {
  const name = holidays.value[date]?.name;
  return holidayDisplayNames.value[name] ?? name;
};

const editDialog = ref(false);
const editingWorkday = ref(null);
const openEditDialog = (workday) => {
  editingWorkday.value = workday;
  editDialog.value = true;
};

const specialHoursDialog = ref(false);
const editingSpecialHours = ref(null);
const editingSpecialHoursWorkday = ref(null);
const openSpecialHoursDialog = (workday, specialHours = null) => {
  editingSpecialHoursWorkday.value = workday;
  editingSpecialHours.value = specialHours;
  specialHoursDialog.value = true;
};

const lineAddDialog = ref(false);
const editingLine = ref({});
const editingLineWorkday = ref({});
const openLineAddDialog = (workday, workdayLine = null) => {
  if (!workdayLine) {
    workdayLine = {};
  }

  editingLine.value = workdayLine;
  editingLineWorkday.value = workday;
  lineAddDialog.value = true;
};

const lockingMonth = ref(false);
const lockMonthDialog = ref(false);
const balanceMutationsDialog = ref(false);

const lockMonth = () => {
  if (!summary.value) return snackbarStore.showMessage(t('timeRegistration.noSummaryAvailable'), "error");
  if (openDays.value.length) return snackbarStore.showMessage(t('timeRegistration.openDaysRemaining'), "error");

  lockMonthDialog.value = true;
};

const confirmLockMonth = async () => {
  lockMonthDialog.value = false;
  lockingMonth.value = true;

  try {
    summary.value.locked = true;
    const lockData = {
      employee_id: route.params.employee,
      year: route.params.year,
      month: route.params.month,
      data: summary.value
    };
    await api.post('workdaySummaryLock', JSON.stringify(lockData));
    snackbarStore.showMessage(t('timeRegistration.monthLockedSuccess'), "success");
    await refreshWorkdays();
  } catch (error) {
    console.log(error);
    snackbarStore.showMessage(t('timeRegistration.monthLockError'), "error");
  } finally {
    lockingMonth.value = false;
  }
};

const addMutationFromLockDialog = () => {
  balanceMutationsDialog.value = true;
};

const openDayCount = computed(() => {
  return workdays.value.filter((day) => day.status === 'new' && day.id).length;
});

const earlierMonthOpen = computed(() => summary.value && !summary.value.locked && summary.value.earlierMonthsLocked === false);

const logsDialog = ref(false);
const selectedWorkdayForLogs = ref(null);

const openLogsDialog = (workday) => {
  if (!workday.id) return;

  selectedWorkdayForLogs.value = workday;
  logsDialog.value = true;
};

const createWorkdayAsHoliday = async (workday) => {
  const collective = isCollectiveLeave(workday.date);
  try {
    const params = {
      date: workday.date,
      employee_id: route.params.employee,
      template: collective ? 'collective-leave' : 'holiday',
    };
    await api.post(`workdayCreate`, params);
    snackbarStore.showMessage(
      collective ? "Collectief verlof succesvol aangemaakt" : "Feestdag succesvol aangemaakt",
      "success",
    );
    await refreshWorkdays();
  } catch (error) {
    console.log(error);
    snackbarStore.showMessage("Er is een fout opgetreden bij het markeren als feestdag", "error");
  }
};

// If hash is set, open the day and scroll to it.
async function handleInitialHash() {
  const hash = route.hash?.slice(1);
  if (!hash) {
    return;
  }

  const dayIndex = workdays.value.findIndex(day => day.date === hash);
  if (dayIndex === -1) {
    return;
  }

  if (!openDays.value.includes(dayIndex)) {
    openDays.value.push(dayIndex);
  }
  // wait for the animation to finish
  await new Promise(resolve => setTimeout(resolve, 200));
  document.querySelector(`[data-day-date='${hash}']`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
}
</script>

<template>
  <v-card>
    <v-card-title class="mt-2 space-between align-center ga-3">
      {{ user?.name ?? t('timeRegistration.title') }}
      <v-icon
        v-if="user?.id"
        icon="mdi-refresh"
        size="small"
        color="primary"
        @click="refreshWorkdays(); refreshProjects()"
      />
      <v-spacer />
      <v-btn
        color="primary"
        v-if="user?.id"
        prepend-icon="mdi-swap-vertical"
        class="mr-2"
        @click="balanceMutationsDialog = true"
      >
        {{ t('timeRegistration.balanceMutations.manage') }}
      </v-btn>
      <v-btn
        color="warning"
        variant="elevated"
        prepend-icon="mdi-lock"
        :loading="lockingMonth"
        :disabled="summary?.locked || openDayCount !== 0"
        @click="lockMonth"
      >
        {{ summary?.locked ? t('timeRegistration.monthLocked') : t('timeRegistration.lockMonth') }}
      </v-btn>
    </v-card-title>
    <v-card-subtitle v-if="user && user.name_accredis && user.name!== user.name_accredis">
      {{ t('timeRegistration.accredis') }}: {{ user.name_accredis }}
    </v-card-subtitle>
  </v-card>
  <v-divider />
  <workday-summary
    :summary="summary"
    :open-day-count="openDayCount"
  />
  <v-divider />
  <v-expansion-panels
    v-model="openDays"
    multiple
    elevation="0"
  >
    <v-expansion-panel
      v-for="(day, index) in workdays"
      :key="day.id"
      :data-day-date="day.date"
    >
      <v-expansion-panel-title
        :class="getClasses(day)"
        :style="holidayTintStyle(day.date)"
      >
        <v-icon
          class="mr-1"
          :color="getIcon(day, true)"
        >
          {{ getIcon(day) }}
        </v-icon>
        <v-icon
          v-if="day.too_late"
          class="mr-1"
          color="indufastRed"
          icon="mdi-clock-alert-outline"
          :title="t('timeRegistration.workday.startedLate')"
        />
        {{ ucFirst(formatDate(day.date)) }}
        <v-icon
          v-if="isNonWorkingDay(day.date)"
          icon="mdi-briefcase-variant-off-outline"
          color="indufastPurple"
          :title="t('timeRegistration.workday.nonWorkingDay')"
          class="ml-3"
        />
        <v-chip
          v-if="getDay(day.date) === 1"
          color="grey"
          class="ml-3"
          :variant="getWeek(day.date) === getWeek(new Date()) ? 'flat' : 'outlined'"
        >
          {{ t('calendar.week') + ' ' + getWeek(day.date, {weekStartsOn: 1}) }}
        </v-chip>
        <v-chip
          v-if="isHoliday(day.date)"
          :color="holidayColor(day.date)"
          class="ml-3"
          variant="outlined"
        >
          {{ getHolidayName(day.date) }}
        </v-chip>
        <v-icon
          v-if="day.id && openDays.indexOf(index) !== -1"
          icon="mdi-pencil"
          class="ml-3"
          :disabled="day.status !== 'new'"
          @click.stop="openEditDialog(day)"
        />
        <v-icon
          v-if="day.id"
          icon="mdi-history"
          class="ml-3"
          :title="t('timeRegistration.workday.showChanges')"
          @click.stop="openLogsDialog(day)"
        />

        <template v-if="!day.id && isHoliday(day.date) && !isClosingDay(day.date) && !summary?.locked">
          <v-btn
            size="small"
            color="primary"
            class="ml-3 mr-1"
            :title="t('timeRegistration.workday.createAsHoliday')"
            @click.stop="createWorkdayAsHoliday(day)"
          >
            {{ t('timeRegistration.workday.createAsHolidayBtn') }}
          </v-btn>
        </template>
        <v-spacer />
        <span v-if="day.status !== 'new' && openDays.indexOf(index) === -1">
          {{ getDaySummary(day) }}
        </span>
      </v-expansion-panel-title>
      <v-expansion-panel-text class="pa-6">
        <div class="summary">
          <template
            v-for="project in getProjectsOnDate(day.date)"
            :key="project.id"
          >
            <h4 class="d-flex align-center ga-2">
              <v-icon
                v-if="hasMultipleProjects(day) && getProjectIndex(day, project) !== -1"
                :icon="`mdi-alpha-${projectLetter(getProjectIndex(day, project))}-circle`"
                :color="projectColor(getProjectIndex(day, project))"
                size="small"
              />
              {{ t('timeRegistration.workday.project') }}
              <router-link
                :to="{ name: '/project/report/[id]', params: { id: project.id } }"
                :title="t('timeRegistration.workday.viewHours')"
              >
                <v-icon
                  color="indufastOrange"
                  icon="mdi-clock"
                  size="small"
                />
              </router-link>
            </h4>
            <v-table>
              <tr>
                <th>{{ t('timeRegistration.workday.fields.name') }}</th>
                <td class="wrap">
                  {{ project.name }}
                </td>
              </tr>
              <tr>
                <th>{{ t('timeRegistration.workday.fields.address') }}</th>
                <td class="wrap">
                  {{ project.address }}
                </td>
              </tr>
              <tr>
                <th>{{ t('timeRegistration.workday.fields.planning') }}</th>
                <td>
                  {{ formatTime(getEventFromProject(project, day.date).start) }} - {{ formatTime(getEventFromProject(project, day.date).end) }}
                </td>
              </tr>
            </v-table>
          </template>

          <h4 v-if="day.workdayDuration">
            {{ t('timeRegistration.workday.workday') }}
          </h4>
          <v-table v-if="day.workdayDuration">
            <tr v-if="!day.work_time">
              <th>{{ t('timeRegistration.workday.fields.workTime') }}</th>
              <td>{{ day.workdayStart ? castTimeForDisplay(day.workdayStart) : '' }} - {{ day.workdayEnd ? castTimeForDisplay(day.workdayEnd) : '' }}</td>
            </tr>
            <tr>
              <th>{{ t('timeRegistration.workday.fields.gross') }}</th>
              <td>
                <span :class="{'overriden': day.work_time}">
                  {{ day.workdayDuration }}
                </span>
              </td>
            </tr>
            <tr>
              <th>{{ t('timeRegistration.workday.fields.break') }}</th>
              <td>
                <span :class="{'overriden': day.break_time}">
                  {{ timeInMinutes(day.break_time || day.workdayPause) }} {{ t('timeRegistration.workday.fields.minutes') }}
                </span>
              </td>
            </tr>
            <tr v-if="day.overtimeBelow">
              <th>{{ t('timeRegistration.workday.fields.overtimeBelow') }}</th>
              <td>{{ day.overtimeBelow }}</td>
            </tr>
            <tr v-if="day.overtimeAbove">
              <th>{{ t('timeRegistration.workday.fields.overtimeAbove') }}</th>
              <td>{{ day.overtimeAbove }}</td>
            </tr>
            <tr class="netDuration">
              <th>{{ t('timeRegistration.workday.fields.net') }}</th>
              <td>
                {{ day.workdayDurationNetClipped }}
              </td>
            </tr>
            <tr
              v-if="day.workdayOutsideWorkingHours"
              class="netDuration"
            >
              <th>{{ t('timeRegistration.workday.fields.shifted') }}</th>
              <td>{{ day.workdayOutsideWorkingHours }}</td>
            </tr>
          </v-table>

          <template v-if="dayHasData(day)">
            <h4>{{ t('timeRegistration.workday.travelTime') }}</h4>
            <v-table>
              <template v-if="day.travel_to_end_line_id || day.travel_to_time || day.travel_to_distance">
                <tr>
                  <th>{{ t('timeRegistration.workday.fields.outbound') }}</th>
                  <td v-if="day.travelToStart && day.travelToEnd">
                    {{ day.travelToStart ? castTimeForDisplay(day.travelToStart) : '' }} - {{ day.travelToEnd ? castTimeForDisplay(day.travelToEnd) : '' }}
                  </td>
                  <td v-else>
                    Onbekend
                  </td>
                </tr>
                <tr>
                  <th>{{ t('timeRegistration.workday.fields.distance') }}</th>
                  <td><span :class="{'overriden': day.travel_to_distance}">{{ formatDistance(day.travelToDistance) }}</span></td>
                </tr>
                <tr v-if="day.travelToDuration">
                  <th>{{ t('timeRegistration.workday.fields.gross') }}</th>
                  <td><span :class="{'overriden': day.travel_to_time}">{{ day.travelToDuration }}</span></td>
                </tr>
                <tr
                  v-if="day.travelToDurationNet"
                  class="netDuration"
                >
                  <th>{{ t('timeRegistration.workday.fields.net') }}</th>
                  <td>{{ day.travelToDurationNet }}</td>
                </tr>
              </template>

              <template v-if="day.travel_from_start_line_id || day.travel_from_time || day.travel_from_distance">
                <tr>
                  <th>{{ t('timeRegistration.workday.fields.return') }}</th>
                  <td v-if="day.travelFromStart && day.travelFromEnd">
                    {{ day.travelFromStart ? castTimeForDisplay(day.travelFromStart) : '' }} - {{ day.travelFromEnd ? castTimeForDisplay(day.travelFromEnd) : '' }}
                  </td>
                  <td v-else>
                    Onbekend
                  </td>
                </tr>
                <tr>
                  <th>{{ t('timeRegistration.workday.fields.distance') }}</th>
                  <td><span :class="{'overriden': day.travel_from_distance}">{{ formatDistance(day.travelFromDistance) }}</span></td>
                </tr>
                <tr v-if="day.travelFromDuration">
                  <th>{{ t('timeRegistration.workday.fields.gross') }}</th>
                  <td><span :class="{'overriden': day.travel_from_time}">{{ day.travelFromDuration }}</span></td>
                </tr>
                <tr
                  v-if="day.travelFromDurationNet"
                  class="netDuration"
                >
                  <th>{{ t('timeRegistration.workday.fields.net') }}</th>
                  <td>{{ day.travelFromDurationNet }}</td>
                </tr>
              </template>

              <tr class="totalDuration">
                <th>{{ t('timeRegistration.workday.fields.total') }}</th>
                <td>{{ day.travelDurationNet }}</td>
              </tr>
            </v-table>
          </template>

          <template v-if="day.specialHoursDuration">
            <h4>{{ t('timeRegistration.workday.specialHours') }}</h4>
            <v-table>
              <tr v-if="day.specialHoursLeave">
                <th>{{ t('timeRegistration.workday.fields.leave') }}</th>
                <td>{{ day.specialHoursLeave }}</td>
              </tr>
              <tr v-if="day.specialHoursSpecialLeave">
                <th>{{ t('timeRegistration.workday.fields.specialLeave') }}</th>
                <td>{{ day.specialHoursSpecialLeave }}</td>
              </tr>
              <tr v-if="day.specialHoursSick">
                <th>{{ t('timeRegistration.workday.fields.sick') }}</th>
                <td>{{ day.specialHoursSick }}</td>
              </tr>
              <tr v-if="day.specialHoursUnexcusedLeave">
                <th>Ongeoorloofd afwezig</th>
                <td>{{ day.specialHoursUnexcusedLeave }}</td>
              </tr>
              <tr v-if="day.specialHoursHoliday">
                <th>Feestdag</th>
                <td>{{ day.specialHoursHoliday }}</td>
              </tr>
              <tr class="totalDuration">
                <th>{{ t('timeRegistration.workday.fields.total') }}</th>
                <td>{{ day.specialHoursDuration }}</td>
              </tr>
            </v-table>
          </template>
        </div>
        <div class="lines">
          <workday-project-split
            v-if="hasMultipleProjects(day)"
            :assignment="getEffectiveAssignment(day)"
            :workday="day"
            :projects="getDayProjects(day)"
          />
          <h4>{{ t('timeRegistration.workday.trips') }}</h4>
          <v-data-table
            :items="day.lines"
            :headers="getDayHeaders(day)"
            item-key="id"
            :row-props="(row) => rowProps(row, day)"
            density="compact"
            :loading="loading[day.date]"
            class="rowHover"
            :items-per-page="-1"
            hide-default-footer
          >
            <template #item.project="{ item }">
              <template v-if="!item.void">
                <v-icon
                  v-for="(project, projectIndex) in getDayProjects(day)"
                  :key="project.id"
                  :icon="`mdi-alpha-${projectLetter(projectIndex)}-circle${getLineProjectIndex(day, item) === projectIndex ? '' : '-outline'}`"
                  :color="getLineProjectIndex(day, item) === projectIndex ? projectColor(projectIndex) : 'grey-lighten-1'"
                  :title="getProjectIconTitle(day, item, project, projectIndex)"
                  :class="{'project-assign': day.status === 'new', 'project-inherited': getLineProjectIndex(day, item) === projectIndex && isProjectInherited(day, item)}"
                  @click="day.status === 'new' && assignLineToProject(day, item, project)"
                />
              </template>
            </template>

            <template #item.start="{ value }">
              {{ value ? castTimeForDisplay(value) : '' }}
              <v-icon
                v-if="isNextDay(value)"
                icon="mdi-calendar-arrow-right"
                size="16"
                :title="t('timeRegistration.workday.tripActions.nextDay')"
                color="indufastBlue"
              />
            </template>

            <template #item.end="{ value }">
              {{ value ? castTimeForDisplay(value) : '' }}
              <v-icon
                v-if="isNextDay(value)"
                icon="mdi-calendar-arrow-right"
                size="16"
                :title="t('timeRegistration.workday.tripActions.nextDay')"
                color="indufastBlue"
              />
            </template>

            <template #item.distance="{ value }">
              {{ value ? formatDistance(value) : '' }}
            </template>

            <template #item.actions="{ item }">
              <v-icon
                :disabled="!item.canBeSetAsTravelToEnd || day.status !== 'new'"
                color="indufastGreen"
                :title="t('timeRegistration.workday.tripActions.setOutbound')"
                @click="setTravelTo(item)"
              >
                mdi-arrow-collapse-down
              </v-icon>
              <v-icon
                :disabled="!item.canBeSetAsTravelFromStart || day.status !== 'new'"
                color="indufastCyan"
                :title="t('timeRegistration.workday.tripActions.setReturn')"
                @click="setTravelFrom(item)"
              >
                mdi-arrow-collapse-up
              </v-icon>
              <v-icon
                :disabled="!item.canBeVoid || day.status !== 'new'"
                color="indufastRed"
                :title="item.void ? t('timeRegistration.workday.tripActions.enableLine') : t('timeRegistration.workday.tripActions.disableLine')"
                @click="voidLine(item)"
              >
                {{ item.void ? 'mdi-undo' : 'mdi-close' }}
              </v-icon>
              <v-icon
                :disabled="day.status !== 'new' || !item.canBeEdited || item.void"
                :title="t('timeRegistration.workday.tripActions.edit')"
                icon="mdi-pencil-outline"
                @click="openLineAddDialog(day, item)"
              />
            </template>
          </v-data-table>

          <v-divider
            v-if="day.specialHours && day.specialHours.length > 0"
            class="my-4"
          />
          <h4
            v-if="day.specialHours && day.specialHours.length > 0"
            class="mb-3"
          >
            {{ t('timeRegistration.workday.specialHours') }}
          </h4>
          <v-data-table
            v-if="day.specialHours && day.specialHours.length > 0"
            :items="day.specialHours"
            :headers="specialHoursHeaders"
            item-key="id"
            :row-props="specialHoursRowProps"
            density="compact"
            :loading="loading[day.date]"
            class="rowHover mb-4"
            :items-per-page="-1"
            hide-default-footer
          >
            <template #item.type="{ value }">
              {{ getSpecialHoursTypeLabel(value) }}
            </template>

            <template #item.actions="{ item }">
              <v-icon
                :disabled="day.status !== 'new' || item.markedForDeletion"
                color="primary"
                :title="t('timeRegistration.workday.specialHoursActions.edit')"
                @click="openSpecialHoursDialog(day, item)"
              >
                mdi-pencil
              </v-icon>
              <v-icon
                :disabled="day.status !== 'new'"
                color="indufastRed"
                :title="item.markedForDeletion ? t('timeRegistration.workday.specialHoursActions.enable') : t('timeRegistration.workday.specialHoursActions.disable')"
                @click="markSpecialHoursForDeletion(day, item)"
              >
                {{ item.markedForDeletion ? 'mdi-undo' : 'mdi-close' }}
              </v-icon>
            </template>
          </v-data-table>
          <div>
            <v-textarea
              v-model="day.remark"
              :label="t('timeRegistration.workday.fields.remarks')"
              variant="outlined"
              :disabled="day.status !== 'new'"
              rows="1"
              auto-grow
              hide-details
              class="mb-2 mt-6"
              @keydown="changedWorkdays[day.date] = true"
            />
          </div>
        </div>
        <v-card-actions v-if="!summary?.locked">
          <v-spacer />
          <v-btn
            prepend-icon="mdi-plus"
            :disabled="day.status !== 'new' || !day.id"
            @click="openLineAddDialog(day)"
          >
            {{ t('timeRegistration.workday.line') }}
          </v-btn>
          <v-btn
            prepend-icon="mdi-clock-plus-outline"
            :disabled="day.status !== 'new' || !day.id"
            class="ml-2"
            @click="openSpecialHoursDialog(day)"
          >
            {{ t('timeRegistration.workday.specialHours') }}
          </v-btn>
          <v-checkbox
            v-model="day.status"
            :label="t('timeRegistration.workday.completed')"
            density="compact"
            false-value="new"
            true-value="processed"
            class="mr-2"
            color="primary"
            hide-details
            :disabled="!day.id"
            @change="changedWorkdays[day.date] = true"
          />
          <v-btn
            color="primary"
            variant="elevated"
            :prepend-icon="day.id ? 'mdi-content-save' : 'mdi-plus'"
            :disabled="day.id && !changedWorkdays[day.date]"
            :loading="loading[day.date]"
            @click="saveWorkday(day)"
          >
            {{ day.id ? t('common.save') : t('timeRegistration.workday.createWorkday') }}
          </v-btn>
        </v-card-actions>
      </v-expansion-panel-text>
    </v-expansion-panel>
  </v-expansion-panels>
  <v-fab
    v-if="Object.keys(changedWorkdays).length"
    size="large"
    color="primary"
    location="top right"
    prepend-icon="mdi-content-save"
    app
    @click="saveAllWorkdays"
  >
    Sla alles op ({{ Object.keys(changedWorkdays).length }})
  </v-fab>

  <workday-edit-dialog
    v-model="editDialog"
    :workday="editingWorkday"
    @saved="refreshWorkdays"
  />

  <special-hours-edit-dialog
    v-model="specialHoursDialog"
    :workday="editingSpecialHoursWorkday"
    :special-hours="editingSpecialHours"
    @saved="refreshSpecialHours"
  />

  <workday-line-dialog
    v-model="lineAddDialog"
    :workday="editingLineWorkday"
    :workday-line="editingLine"
    @saved="updateWorkday(editingLineWorkday, $event)"
  />

  <lock-month-dialog
    v-model="lockMonthDialog"
    :year="route.params.year"
    :month="route.params.month"
    :summary="summary"
    :loading="lockingMonth"
    :earlierMonthOpen="earlierMonthOpen"
    @confirm="confirmLockMonth"
    @add-mutation="addMutationFromLockDialog"
  />

  <balance-mutations-dialog
    v-model="balanceMutationsDialog"
    :employee-id="route.params.employee"
    :year="route.params.year"
    :month="route.params.month"
    :locked="!!summary?.locked"
    @saved="refreshWorkdaySummary"
  />

  <v-dialog
    v-model="logsDialog"
    max-width="800"
    scrollable
  >
    <v-card>
      <v-card-title class="bg-primary">
        <v-icon class="mr-2">
          mdi-history
        </v-icon>
        Wijzigingen - {{ selectedWorkdayForLogs ? ucFirst(formatDate(selectedWorkdayForLogs.date)) : '' }}
        <v-spacer />
      </v-card-title>
      <v-card-text>
        <logs-display
          entity-name="IndufastWorkday"
          :entity-id="selectedWorkdayForLogs?.id"
        />
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<style lang="scss">
tr.workday-line td:first-child {
  border-left: 10px solid lightgray;
}

tr.void td:first-child {
  border-left: 10px solid rgba(var(--v-theme-indufastRed), .75);
}

tr.void {
  background-color: rgba(var(--v-theme-indufastRed), .1);
}

tr.travel-to td:first-child {
  border-left: 10px solid rgba(var(--v-theme-indufastGreen), .75);
}

tr.travel-to {
  background-color: rgba(var(--v-theme-indufastGreen), .1);
}

tr.travel-from td:first-child {
  border-left: 10px solid rgba(var(--v-theme-indufastCyan), .75);
}

tr.travel-from {
  background-color: rgba(var(--v-theme-indufastCyan), .1);
}

// Keep the color of the row on hover, a bit darker, instead of the grey of .rowHover.
.rowHover tr.void:hover td {
  background-color: rgba(var(--v-theme-indufastRed), .2);
}

.rowHover tr.travel-to:hover td {
  background-color: rgba(var(--v-theme-indufastGreen), .2);
}

.rowHover tr.travel-from:hover td {
  background-color: rgba(var(--v-theme-indufastCyan), .2);
}

tr.project-start td {
  border-top: 3px solid rgb(var(--project-color)) !important;
}

.project-assign {
  cursor: pointer;
}

.project-inherited {
  opacity: .45;
}

tr.special-hours-line td:first-child {
  border-left: 10px solid lightgray;
}

tr.marked-for-deletion td:first-child {
  border-left: 10px solid rgba(var(--v-theme-indufastRed), .75);
}

tr.marked-for-deletion {
  background-color: rgba(var(--v-theme-indufastRed), .1);
}

.v-icon--disabled {
  color: grey !important;
}

.workday-title {
  background-color: rgba(var(--v-theme-indufastCyan), .1);
}

td.no-wrap {
  white-space: nowrap;
  width: 0;
}
tr.netDuration,
tr.totalDuration {
  font-weight: bold;
}

div.summary {
  width: 300px;
  display: inline-block;
  vertical-align: top;
}

div.lines {
  width: calc(100% - 300px);
  display: inline-block;
  vertical-align: top;
}

.weekend {
  background-color: rgba(var(--v-theme-indufastYellow), var(--v-extra-light-opacity));
}

.new .v-expansion-panel-title:not(.v-expansion-panel-title--active) {
  color: grey;
}
.overriden {
  background-color: rgba(var(--v-theme-indufastRed), var(--v-extra-light-opacity));
  border-radius: 5px;
  padding-left: 5px;
  padding-right: 5px;
}
</style>
